import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Api } from '../../../../services/interceptor';
import { QUERY_KEY } from '../../../../services/queryEndPoint';
import {
  TICKER_INTERVAL_OPTIONS,
  type ITickerConfigType,
  type TickerIntervalType,
  type TickerRowState,
} from '../tournamentsDetails/matches/tickerAutomationConstants';
import '../tournamentsDetails/matches/TickerAutomationModal.scss';
import './TournamentTickerConfigSection.scss';

const EVENT_DRIVEN_TYPES = new Set([
  'Wicket', 'Four', 'Six', 'PlayerOut', 'Hatrick',
  'TossResult', 'BatsmanMultipleOfFifty', 'TeaBreak',
  'LunchBreak', 'BadLights', 'NewBall', 'InningEndSummary',
]);

interface Props {
  initial?: TickerRowState[];
  onChange: (rows: TickerRowState[]) => void;
}

export default function TournamentTickerConfigSection({ initial, onChange }: Props) {
  const [rows, setRows] = useState<TickerRowState[]>(initial ?? []);

  const { data: configTypes, isLoading } = useQuery<ITickerConfigType[]>({
    queryKey: [QUERY_KEY.getTickerConfigTypes],
    queryFn: async () => (await Api.get<ITickerConfigType[]>(QUERY_KEY.getTickerConfigTypes)).data,
    staleTime: 5 * 60 * 1000,
  });

  const initialRows = useMemo<TickerRowState[]>(() => {
    if (!configTypes?.length) return [];
    const existing = new Map((initial ?? []).map((r) => [r.graphicType, r]));
    return configTypes.map((ct) => {
      const prev = existing.get(ct.graphicType);
      return {
        graphicType: ct.graphicType,
        graphicTitle: ct.graphicTitle,
        automated: prev?.automated ?? false,
        intervalType: prev?.intervalType ?? ct.intervalType,
        intervalValue: prev?.intervalValue ?? ct.intervalValue,
        hasInterval: !EVENT_DRIVEN_TYPES.has(ct.graphicType),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configTypes]);

  useEffect(() => {
    if (initialRows.length > 0 && rows.length === 0) {
      setRows(initialRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRows]);

  useEffect(() => {
    onChange(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const updateRow = (graphicType: string, patch: Partial<TickerRowState>) => {
    setRows((prev) => prev.map((r) => (r.graphicType === graphicType ? { ...r, ...patch } : r)));
  };

  if (isLoading) return <p className="ticker-loading">Loading graphics…</p>;
  if (rows.length === 0) return <p className="ticker-loading">No ticker types available.</p>;

  return (
    <div className="tournament-ticker-section">
      <h3 className="tournament-ticker-section-title">Ticker Automation</h3>
      <div className="ticker-automation-list-header">
        <span>Graphic</span>
        <span>Interval</span>
        <span>Automate</span>
      </div>
      <div className="ticker-automation-list">
        {rows.map((row) => (
          <div className="ticker-row" key={row.graphicType}>
            <span className="ticker-row-label">{row.graphicTitle}</span>

            <div className="ticker-interval-controls">
              {row.hasInterval ? (
                <>
                  <input
                    type="number"
                    min={1}
                    className="ticker-interval-input"
                    value={row.intervalValue}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      if (Number.isFinite(n) && n >= 1) updateRow(row.graphicType, { intervalValue: n });
                    }}
                    aria-label="Interval value"
                  />
                  <select
                    className="ticker-interval-select"
                    value={row.intervalType}
                    onChange={(e) =>
                      updateRow(row.graphicType, { intervalType: e.target.value as TickerIntervalType })
                    }
                    aria-label="Interval type"
                  >
                    {TICKER_INTERVAL_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <span className="ticker-event-driven">On event</span>
              )}
            </div>

            <label className="ticker-toggle-switch">
              <input
                type="checkbox"
                checked={row.automated}
                onChange={() => updateRow(row.graphicType, { automated: !row.automated })}
              />
              <span className="ticker-slider" />
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}
